import { forwardRef, type SelectHTMLAttributes } from 'react';
import { cn } from '../cn';

export interface SelectOption<V extends string = string> {
  value: V;
  label: string;
  disabled?: boolean;
}
export interface SelectProps<V extends string = string> extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange' | 'value' | 'size'> {
  value: V;
  options: readonly SelectOption<V>[];
  onChange: (v: V) => void;
  size?: 'sm' | 'md' | 'responsive';
  block?: boolean;
}

/** Native select styled as the design: 32 px, 10 px padding, radius 7, bg2, border, 500 12px (mobile 34 / radius 8 / 13px). */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select({ value, options, onChange, size = 'responsive', block, className, ...rest }, ref) {
  return (
    <select
      ref={ref}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={cn(
        'border border-border bg-bg2 px-2.5 font-medium text-fg outline-none disabled:opacity-50',
        size === 'sm' && 'h-7 rounded px-2 text-sm',
        size === 'md' && 'h-8 rounded-md text-base',
        size === 'responsive' && 'h-[34px] rounded-lg text-md md:h-8 md:rounded-md md:text-base',
        block ? 'w-full' : 'max-w-[200px] md:max-w-none',
        className,
      )}
      {...rest}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value} disabled={o.disabled}>
          {o.label}
        </option>
      ))}
    </select>
  );
}) as <V extends string = string>(p: SelectProps<V> & { ref?: React.Ref<HTMLSelectElement> }) => JSX.Element;
